import type { ComponentProps } from 'react';
import React, { memo } from 'react';
import { StyleSheet } from 'react-native';
import { LI, UL } from '@expo/html-elements';
import { theme, useResponsive } from '@sg/garnish';

import {
  LocationResultCardLoading,
  LocationResultsCard,
} from '../../../LocationResultCard';

// ─────────────────────────────────────────────────────────────────────────────

export const LocationResultsList = memo((props: LocationResultsListProps) => {
  const {
    locations,
    locationSearchType,
    withDetailsModal,
    interactedLocationRestaurantSlug,
    onCardPress,
    onCardLayout,
    onCardFocus,
    onCardUnmount,
    isLoading,
  } = props;

  const { currentBreakpoint } = useResponsive();

  // ─── Loading View ────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <UL>
        {CARD_LOADING_PLACEHOLDERS.map((placeholder) => (
          <LI key={placeholder} style={styles.listItem}>
            <LocationResultCardLoading />
          </LI>
        ))}
      </UL>
    );
  }

  // ─── Loaded View ─────────────────────────────────────────────────────

  return (
    <UL>
      {locations.map((location, index) => {
        const { id, restaurantSlug } = location;

        const isSingleItem = locations.length === 0;
        const isLastItem = index === locations.length - 1;
        const isInteracted =
          interactedLocationRestaurantSlug !== undefined &&
          restaurantSlug !== undefined &&
          interactedLocationRestaurantSlug === restaurantSlug;
        const isWithBottomBorder =
          currentBreakpoint.isXS && !isSingleItem && !isLastItem;

        return (
          <LI key={id} style={styles.listItem}>
            <LocationResultsCard
              location={location}
              withBottomBorder={isWithBottomBorder}
              locationSearchType={locationSearchType}
              withDetailsModal={withDetailsModal}
              isLoading={isInteracted}
              isDisabled={isInteracted}
              onLayout={onCardLayout}
              onUmount={onCardUnmount}
              onPress={onCardPress}
              onFocus={onCardFocus}
            />
          </LI>
        );
      })}
    </UL>
  );
});

// ─── Constants ───────────────────────────────────────────────────────────────

const CARD_LOADING_PLACEHOLDERS = [1, 2, 3, 4, 5];

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  listItem: {
    paddingHorizontal: theme.spacing['4'],
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type LocationResultsListProps = {
  readonly locations: ReadonlyArray<LocationResultCardProps['location']>;
  readonly locationSearchType: 'pickup' | 'delivery' | 'outpost';
  readonly isLoading?: boolean;

  /**
   * While interacting with a location card (reordering, navigation, etc.),
   * we occasionally need to display the loading, disabled states.
   * To display the corresponding additional state, we match the target location
   * card restaurant slug to the interacted location restaurant slug.
   * */
  readonly interactedLocationRestaurantSlug?: string;

  readonly onCardPress: LocationResultCardProps['onPress'];
  readonly onCardLayout: LocationResultCardProps['onLayout'];
  readonly onCardFocus?: LocationResultCardProps['onFocus'];
  readonly onCardUnmount?: LocationResultCardProps['onUmount'];
} & Pick<LocationResultCardProps, 'withDetailsModal'>;

type LocationResultCardProps = ComponentProps<typeof LocationResultsCard>;
